<template>
  <div class="ppm-backdrop" @click.self="onClose">
    <div class="ppm-modal" role="dialog" aria-modal="true" aria-label="Attach photos">
      <header class="ppm-header">
        <div>
          <div class="ppm-eyebrow">Attach photos</div>
          <div class="ppm-title" v-if="patient">
            {{ patient.fName }} {{ patient.lName }}
          </div>
        </div>
        <button class="ppm-close" type="button" aria-label="Close" @click="onClose">
          <i class="fa fa-times"></i>
        </button>
      </header>

      <!-- The one thing a TC has to understand about this feature, said once,
           where they are actually working. -->
      <p class="ppm-scope">
        <i class="fa fa-info-circle"></i>
        Photos belong to the patient, not to one plan — every treatment plan for
        {{ patientFirstName }} can show them. Tag teeth to control which plans a photo appears on.
      </p>

      <div class="ppm-body">
        <div v-if="error" class="ppm-error">{{ error }}</div>

        <label
          class="ppm-drop"
          :class="{ 'ppm-drop-over': dragOver, 'ppm-drop-disabled': atLimit || busy }"
          @dragover.prevent="dragOver = true"
          @dragleave.prevent="dragOver = false"
          @drop.prevent="onDrop"
        >
          <input
            ref="fileInput"
            type="file"
            multiple
            accept="image/jpeg,image/png,image/webp,image/gif"
            class="ppm-file-input"
            :disabled="atLimit || busy"
            @change="onFilesSelected"
          />
          <i class="fa fa-camera ppm-drop-icon"></i>
          <span class="ppm-drop-main" v-if="atLimit">
            This patient has the maximum of {{ maxPhotos }} photos.
          </span>
          <span class="ppm-drop-main" v-else>
            Drop photos here, or <strong>browse</strong>
          </span>
          <span class="ppm-drop-sub">JPG, PNG, WEBP or GIF &middot; up to 10MB each</span>
        </label>

        <div v-if="uploadQueue.length > 0" class="ppm-queue">
          <div v-for="q in uploadQueue" :key="q.id" class="ppm-queue-row">
            <i class="fa fa-circle-o-notch fa-spin" v-if="!q.failed"></i>
            <i class="fa fa-exclamation-triangle ppm-queue-fail" v-else></i>
            <span class="ppm-queue-name">{{ q.name }}</span>
            <span class="ppm-queue-status">{{ q.failed ? q.error : 'Uploading…' }}</span>
          </div>
        </div>

        <div v-if="loading" class="ppm-loading">
          <i class="fa fa-circle-o-notch fa-spin"></i> Loading photos…
        </div>

        <p v-else-if="photos.length === 0" class="ppm-empty">
          No photos yet.
        </p>

        <div v-else class="ppm-list">
          <div v-for="photo in photos" :key="photo.photoId" class="ppm-card">
            <div class="ppm-card-top">
              <button
                type="button"
                class="ppm-thumb-btn"
                aria-label="View full size"
                @click="$emit('preview', photo)"
              >
                <img :src="photo.url" :alt="photo.fileName" class="ppm-thumb" @error="onThumbError" />
              </button>

              <div class="ppm-card-meta">
                <div class="ppm-card-name" :title="photo.fileName">{{ photo.fileName || 'Photo' }}</div>
                <div class="ppm-card-teeth">
                  <template v-if="teethOf(photo).length > 0">
                    <span v-for="t in teethOf(photo)" :key="t" class="ppm-tooth-chip">#{{ t }}</span>
                  </template>
                  <span v-else class="ppm-card-untagged">
                    No teeth tagged — shows on every plan
                  </span>
                </div>
                <div class="ppm-card-actions">
                  <button type="button" class="ppm-link" @click="toggleTagging(photo.photoId)">
                    <i class="fa" :class="isTagging(photo.photoId) ? 'fa-chevron-up' : 'fa-pencil'"></i>
                    {{ isTagging(photo.photoId) ? 'Done' : 'Tag teeth' }}
                  </button>
                  <button
                    type="button"
                    class="ppm-link ppm-link-danger"
                    :disabled="deletingId === photo.photoId"
                    @click="onDeleteClick(photo)"
                  >
                    <i class="fa" :class="deletingId === photo.photoId ? 'fa-circle-o-notch fa-spin' : 'fa-trash-o'"></i>
                    {{ confirmingId === photo.photoId ? 'Tap again to delete' : 'Delete' }}
                  </button>
                </div>
              </div>
            </div>

            <div v-if="isTagging(photo.photoId)" class="ppm-tagger">
              <ToothSelector
                :value="teethOf(photo)"
                :disabled="savingTeethId === photo.photoId"
                @input="onTeethChange(photo, $event)"
              />
              <div class="ppm-tagger-status" v-if="savingTeethId === photo.photoId">
                <i class="fa fa-circle-o-notch fa-spin"></i> Saving…
              </div>
            </div>
          </div>
        </div>
      </div>

      <footer class="ppm-footer">
        <span class="ppm-count">{{ photos.length }} of {{ maxPhotos }}</span>
        <button type="button" class="ppm-done" @click="onClose">Done</button>
      </footer>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import ToothSelector from './ToothSelector.vue';
import { validateFile } from '@/utils/fileUpload';
import { extractApiError } from '@/utils/apiError';

/**
 * The Attach Photos manager — the single place a patient's photo library is
 * built and maintained. Opened from Build mode and from Present Mode; the same
 * component either way, because the library is the same library.
 *
 * Uploads run one at a time rather than in parallel. Each file needs its own
 * presigned POST, and a chairside tablet on office wifi dropping six 8MP
 * photos at once is exactly where parallel uploads start timing out. Serial is
 * slower on paper and finishes more often.
 */
export default {
  name: 'PatientPhotosModal',
  components: { ToothSelector },
  props: {
    siteID: { type: String, required: true },
    patNum: { type: [Number, String], required: true },
    patient: { type: Object, default: null },
  },
  data() {
    return {
      dragOver: false,
      uploadQueue: [],
      error: null,
      taggingIds: [],
      savingTeethId: null,
      deletingId: null,
      // Two-tap delete instead of a confirm() — this runs on a tablet mid
      // appointment, where a blocking browser dialog is worse than a second tap.
      confirmingId: null,
      confirmTimer: null,
      maxPhotos: 100,
      queueSeq: 0,
    };
  },
  computed: {
    ...mapState('txPresenter', {
      photos: (s) => s.patientPhotos.list,
      loading: (s) => s.patientPhotos.loading,
      storeError: (s) => s.patientPhotos.error,
    }),
    busy() {
      return this.uploadQueue.some((q) => !q.failed);
    },
    atLimit() {
      return this.photos.length >= this.maxPhotos;
    },
    patientFirstName() {
      return (this.patient && this.patient.fName) || 'this patient';
    },
    employeeID() {
      const emp = this.$store.state.customer && this.$store.state.customer.employee;
      return (emp && (emp.EmployeeID || emp.employeeID)) || null;
    },
  },
  watch: {
    storeError(v) {
      if (v) this.error = v;
    },
  },
  created() {
    this.$store.dispatch('txPresenter/loadPatientPhotos', {
      siteID: this.siteID,
      patNum: this.patNum,
    });
  },
  beforeDestroy() {
    if (this.confirmTimer) clearTimeout(this.confirmTimer);
  },
  methods: {
    teethOf(photo) {
      return ((photo && photo.teeth) || []).slice().sort((a, b) => a - b);
    },
    isTagging(photoId) {
      return this.taggingIds.indexOf(photoId) !== -1;
    },
    toggleTagging(photoId) {
      this.taggingIds = this.isTagging(photoId)
        ? this.taggingIds.filter((id) => id !== photoId)
        : this.taggingIds.concat(photoId);
    },
    onClose() {
      this.$emit('close');
    },
    onThumbError(e) {
      // A presigned GET that aged out while the modal sat open. Re-listing
      // re-signs every url in one call.
      if (e && e.target) e.target.style.visibility = 'hidden';
      this.$store.dispatch('txPresenter/loadPatientPhotos', {
        siteID: this.siteID,
        patNum: this.patNum,
        force: true,
      });
    },
    onDrop(e) {
      this.dragOver = false;
      if (this.atLimit || this.busy) return;
      this.processFiles((e.dataTransfer && e.dataTransfer.files) || []);
    },
    onFilesSelected(e) {
      this.processFiles((e.target && e.target.files) || []);
      // Let the same file be picked again after a failed attempt.
      if (this.$refs.fileInput) this.$refs.fileInput.value = '';
    },
    async processFiles(fileList) {
      this.error = null;
      const files = Array.from(fileList || []);

      for (const file of files) {
        if (this.photos.length + this.uploadQueue.filter((q) => !q.failed).length >= this.maxPhotos) {
          this.error = `Only ${this.maxPhotos} photos can be attached to one patient. The rest were skipped.`;
          break;
        }

        const check = validateFile(file);
        if (!check.valid) {
          this.pushFailed(file.name, check.error);
          continue;
        }
        if (String(file.type || '').toLowerCase().indexOf('image/') !== 0) {
          this.pushFailed(file.name, 'Only photos can be attached.');
          continue;
        }

        const entry = { id: ++this.queueSeq, name: file.name, failed: false, error: null };
        this.uploadQueue.push(entry);

        try {
          await this.$store.dispatch('txPresenter/uploadPatientPhoto', {
            siteID: this.siteID,
            patNum: this.patNum,
            file,
            teeth: [],
            setBy: this.employeeID,
          });
          this.uploadQueue = this.uploadQueue.filter((q) => q.id !== entry.id);
        } catch (err) {
          console.error('[PatientPhotosModal] upload failed:', err);
          entry.failed = true;
          entry.error = extractApiError(err, 'Upload failed.');
        }
      }
    },
    pushFailed(name, message) {
      const entry = { id: ++this.queueSeq, name, failed: true, error: message };
      this.uploadQueue.push(entry);
    },
    async onTeethChange(photo, teeth) {
      this.savingTeethId = photo.photoId;
      this.error = null;
      try {
        await this.$store.dispatch('txPresenter/updatePhotoTeeth', {
          siteID: this.siteID,
          photoId: photo.photoId,
          teeth,
        });
      } catch (err) {
        console.error('[PatientPhotosModal] retag failed:', err);
        this.error = extractApiError(err, "Couldn't save the teeth for that photo.");
      } finally {
        this.savingTeethId = null;
      }
    },
    onDeleteClick(photo) {
      if (this.confirmingId !== photo.photoId) {
        this.confirmingId = photo.photoId;
        if (this.confirmTimer) clearTimeout(this.confirmTimer);
        this.confirmTimer = setTimeout(() => { this.confirmingId = null; }, 4000);
        return;
      }
      this.confirmingId = null;
      if (this.confirmTimer) clearTimeout(this.confirmTimer);
      this.doDelete(photo);
    },
    async doDelete(photo) {
      this.deletingId = photo.photoId;
      this.error = null;
      try {
        await this.$store.dispatch('txPresenter/deletePatientPhoto', {
          siteID: this.siteID,
          photoId: photo.photoId,
        });
        this.taggingIds = this.taggingIds.filter((id) => id !== photo.photoId);
      } catch (err) {
        console.error('[PatientPhotosModal] delete failed:', err);
        this.error = extractApiError(err, "Couldn't delete that photo.");
      } finally {
        this.deletingId = null;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.ppm-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.42);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 28px;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, system-ui, sans-serif;
}

.ppm-modal {
  background: #fff;
  width: 100%;
  max-width: 720px;
  max-height: calc(100vh - 56px);
  border-radius: 14px;
  box-shadow: 0 20px 50px rgba(15, 23, 42, 0.18), 0 4px 12px rgba(15, 23, 42, 0.06);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.ppm-modal * { box-sizing: border-box; }

.ppm-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 18px 20px 14px;
  border-bottom: 1px solid #e4e7ec;
}

.ppm-eyebrow {
  font-size: 10.5px;
  text-transform: uppercase;
  letter-spacing: 1.2px;
  color: #94a3b8;
  font-weight: 600;
}

.ppm-title {
  font-size: 16px;
  font-weight: 600;
  color: #0f172a;
  margin-top: 3px;
}

.ppm-close {
  border: none;
  background: none;
  color: #94a3b8;
  font-size: 16px;
  cursor: pointer;
  padding: 2px 4px;

  &:hover { color: #475569; }
}

.ppm-scope {
  margin: 0;
  padding: 10px 20px;
  background: #f0f9f9;
  border-bottom: 1px solid #d9eded;
  font-size: 12.5px;
  line-height: 1.5;
  color: #256a6a;

  i { margin-right: 5px; }
}

.ppm-body {
  padding: 16px 20px;
  overflow-y: auto;
  flex: 1 1 auto;
}

.ppm-error {
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #b91c1c;
  border-radius: 8px;
  padding: 9px 12px;
  font-size: 13px;
  margin-bottom: 12px;
}

.ppm-drop {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  border: 1.5px dashed #cbd5e1;
  border-radius: 10px;
  padding: 22px 16px;
  cursor: pointer;
  text-align: center;
  transition: border-color 0.12s ease, background-color 0.12s ease;
  margin-bottom: 14px;

  &:hover { border-color: #17a2a2; }
}

.ppm-drop-over {
  border-color: #17a2a2;
  background: #f0f9f9;
}

.ppm-drop-disabled {
  opacity: 0.6;
  cursor: default;
  &:hover { border-color: #cbd5e1; }
}

.ppm-file-input {
  display: none;
}

.ppm-drop-icon {
  font-size: 22px;
  color: #94a3b8;
}

.ppm-drop-main {
  font-size: 13.5px;
  color: #475569;

  strong { color: #17a2a2; }
}

.ppm-drop-sub {
  font-size: 11.5px;
  color: #94a3b8;
}

.ppm-queue {
  margin-bottom: 12px;
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.ppm-queue-row {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12.5px;
  color: #475569;
  background: #f8fafc;
  border-radius: 6px;
  padding: 6px 10px;
}

.ppm-queue-fail { color: #d97706; }

.ppm-queue-name {
  flex: 1 1 auto;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ppm-queue-status {
  flex: 0 0 auto;
  color: #94a3b8;
  font-size: 11.5px;
}

.ppm-loading,
.ppm-empty {
  text-align: center;
  color: #94a3b8;
  font-size: 13px;
  padding: 18px 0;
  margin: 0;
}

.ppm-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.ppm-card {
  border: 1px solid #e4e7ec;
  border-radius: 10px;
  padding: 12px;
}

.ppm-card-top {
  display: flex;
  gap: 12px;
  align-items: flex-start;
}

.ppm-thumb-btn {
  flex: 0 0 auto;
  border: none;
  background: none;
  padding: 0;
  cursor: pointer;
  border-radius: 8px;
  overflow: hidden;
  line-height: 0;

  &:focus-visible { outline: 2px solid #17a2a2; outline-offset: 2px; }
}

.ppm-thumb {
  width: 92px;
  height: 70px;
  object-fit: cover;
  background: #f1f5f9;
  display: block;
}

.ppm-card-meta {
  flex: 1 1 auto;
  min-width: 0;
}

.ppm-card-name {
  font-size: 13px;
  font-weight: 600;
  color: #0f172a;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ppm-card-teeth {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin: 6px 0 8px;
}

.ppm-tooth-chip {
  background: #e6f7f7;
  color: #0f7c7c;
  border-radius: 10px;
  padding: 1px 8px;
  font-size: 11px;
  font-weight: 600;
}

.ppm-card-untagged {
  font-size: 11.5px;
  color: #94a3b8;
  font-style: italic;
}

.ppm-card-actions {
  display: flex;
  gap: 14px;
}

.ppm-link {
  border: none;
  background: none;
  padding: 0;
  font-size: 12px;
  font-weight: 600;
  color: #17a2a2;
  cursor: pointer;

  i { margin-right: 4px; }

  &:disabled { opacity: 0.5; cursor: default; }
}

.ppm-link-danger { color: #dc2626; }

.ppm-tagger {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #eef1f5;
}

.ppm-tagger-status {
  font-size: 11.5px;
  color: #94a3b8;
  margin-top: 6px;
}

.ppm-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  border-top: 1px solid #e4e7ec;
}

.ppm-count {
  font-size: 12px;
  color: #94a3b8;
}

.ppm-done {
  border: none;
  background: #17a2a2;
  color: #fff;
  border-radius: 7px;
  padding: 8px 20px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;

  &:hover { background: #148f8f; }
}
</style>
